const REQUIRED_KEYS = [
  'schema_version',
  'product_id',
  'display_title',
  'default_unit',
  'available_units',
  'sizes',
  'measurement_columns',
  'tables',
  'how_to_measure',
  'between_sizes_copy',
];

const CANONICAL_BETWEEN_SIZES_COPY =
  'If you are between sizes, we recommend choosing the larger size for the best fit.';

function assert(condition, message) {
  if (!condition) throw new Error(message);
}

export function validateRosselleSizeGuidePayload(payload) {
  assert(payload && typeof payload === 'object' && !Array.isArray(payload), 'Payload must be an object.');
  assert(
    Object.keys(payload).sort().join('|') === [...REQUIRED_KEYS].sort().join('|'),
    'Payload fields are missing or unsupported.'
  );
  assert(payload.schema_version === '1.0', 'Unsupported storefront schema.');
  assert(typeof payload.product_id === 'string' && payload.product_id, 'Product ID is missing.');
  assert(typeof payload.display_title === 'string' && payload.display_title, 'Display title is missing.');
  assert(payload.default_unit === 'IN', 'Default unit must be IN.');
  assert(
    JSON.stringify(payload.available_units) === JSON.stringify(['IN', 'CM']),
    'Available units must be IN and CM.'
  );
  assert(
    Array.isArray(payload.sizes) && payload.sizes.length > 0 && new Set(payload.sizes).size === payload.sizes.length,
    'Sizes are missing or duplicated.'
  );
  assert(Array.isArray(payload.measurement_columns) && payload.measurement_columns.length > 0, 'Columns are missing.');

  const ids = payload.measurement_columns.map((column) => column?.id);
  const labels = payload.measurement_columns.map((column) => column?.label);
  assert(ids.every((value) => typeof value === 'string' && value) && new Set(ids).size === ids.length, 'Column IDs are invalid.');
  assert(labels.every((value) => typeof value === 'string' && value), 'Column labels are invalid.');
  assert(payload.tables && Object.keys(payload.tables).sort().join('|') === 'CM|IN', 'Both unit tables are required.');

  for (const unit of payload.available_units) {
    const rows = payload.tables[unit];
    assert(Array.isArray(rows) && rows.length === payload.sizes.length, `${unit} table is incomplete.`);
    rows.forEach((row, index) => {
      assert(row && Object.keys(row).sort().join('|') === 'size|values', `${unit} row is malformed.`);
      assert(row.size === payload.sizes[index], `${unit} size order is invalid.`);
      assert(
        row.values && Object.keys(row.values).sort().join('|') === [...ids].sort().join('|'),
        `${unit} measurement identities are invalid.`
      );
      assert(
        Object.values(row.values).every((value) => typeof value === 'string' && value),
        `${unit} contains a missing display value.`
      );
    });
  }

  assert(
    Array.isArray(payload.how_to_measure) && payload.how_to_measure.length === ids.length,
    'Measuring instructions are incomplete.'
  );
  assert(
    payload.how_to_measure.map((item) => item?.measurement_id).join('|') === ids.join('|'),
    'Measuring instructions are out of order.'
  );
  assert(
    payload.how_to_measure.every(
      (item, index) =>
        item.label === labels[index] && typeof item.instruction === 'string' && item.instruction
    ),
    'Measuring instructions are malformed.'
  );
  assert(payload.between_sizes_copy === CANONICAL_BETWEEN_SIZES_COPY, 'Between-sizes copy is not canonical.');
  return true;
}

function createElement(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

class RosselleSizeGuide extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    this.#initialize();
    this.closest('.shopify-section')?.addEventListener('variant:update', this.#handleVariantUpdate, {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.closest('variant-picker')?.removeEventListener('change', this.#handleVariantChange);
  }

  #handleVariantUpdate = () => {
    this.#initialize();
  };

  #initialize() {
    const previousUnit = this.state?.unit;

    try {
      const source = this.querySelector('[data-rosselle-size-guide-payload]');
      const payload = JSON.parse(source?.textContent || '');
      validateRosselleSizeGuidePayload(payload);
      this.payload = payload;
      this.state = {
        unit: payload.available_units.includes(previousUnit) ? previousUnit : payload.default_unit,
        activeSize: null,
        activeMeasurement: null,
        preselectedSize: this.#selectedSize(),
      };
      this.#render();
      const trigger = document.getElementById(this.dataset.triggerId);
      if (trigger) trigger.hidden = false;
      const picker = this.closest('variant-picker');
      picker?.removeEventListener('change', this.#handleVariantChange);
      picker?.addEventListener('change', this.#handleVariantChange);
    } catch (_error) {
      this.dataset.state = 'invalid';
      this.hidden = true;
    }
  }

  #handleVariantChange = () => {
    this.state.preselectedSize = this.#selectedSize();
    this.#applyInteractionClasses();
  };

  #selectedSize() {
    const picker = this.closest('variant-picker');
    const checked = picker?.querySelector('input[type="radio"]:checked');
    if (checked && /size/i.test(checked.name) && this.payload?.sizes.includes(checked.value)) return checked.value;
    const select = [...(picker?.querySelectorAll('select') || [])].find((node) => /size/i.test(node.name));
    return select && this.payload?.sizes.includes(select.value) ? select.value : null;
  }

  #render() {
    this.querySelector('.rosselle-size-guide__subtitle').textContent = `For ${this.payload.display_title}`;
    this.querySelector('.rosselle-size-guide__between').textContent = this.payload.between_sizes_copy;
    this.#renderUnits();
    this.#renderTable();
    this.#renderInstructions();
    this.dataset.state = 'ready';
  }

  #renderUnits() {
    const group = this.querySelector('.rosselle-size-guide__units');
    group.replaceChildren();
    this.payload.available_units.forEach((unit, index) => {
      const button = createElement('button', 'button-unstyled rosselle-size-guide__unit', unit);
      button.type = 'button';
      button.setAttribute('aria-pressed', this.state.unit === unit ? 'true' : 'false');
      if (this.state.unit === unit) button.classList.add('is-current');
      button.addEventListener('click', () => {
        this.state.unit = unit;
        this.#updateUnitState();
        this.#renderTable();
      });
      group.append(button);
      if (index === 0) group.append(createElement('span', 'rosselle-size-guide__unit-separator', '|'));
    });
  }

  #updateUnitState() {
    this.querySelectorAll('.rosselle-size-guide__unit').forEach((button) => {
      const isCurrent = button.textContent === this.state.unit;
      button.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
      button.classList.toggle('is-current', isCurrent);
    });
  }

  #renderTable() {
    const region = this.querySelector('.rosselle-size-guide__table-scroll');
    region.replaceChildren();
    const table = createElement('table', 'rosselle-size-guide__table');
    table.setAttribute('aria-label', `Size chart in ${this.state.unit}`);
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    const sizeHeading = createElement('th', 'rosselle-size-guide__size-heading', 'Size');
    sizeHeading.scope = 'col';
    headerRow.append(sizeHeading);
    this.payload.measurement_columns.forEach((column) => {
      const heading = createElement('th', '', column.label);
      heading.scope = 'col';
      heading.dataset.measurement = column.id;
      headerRow.append(heading);
    });
    thead.append(headerRow);

    const tbody = document.createElement('tbody');
    this.payload.tables[this.state.unit].forEach((row) => {
      const tr = document.createElement('tr');
      tr.dataset.size = row.size;
      const sizeCell = createElement('th', 'rosselle-size-guide__size-cell', row.size);
      sizeCell.scope = 'row';
      sizeCell.dataset.size = row.size;
      tr.append(sizeCell);

      this.payload.measurement_columns.forEach((column) => {
        const cell = createElement('td', 'rosselle-size-guide__data-cell', row.values[column.id]);
        cell.tabIndex = 0;
        cell.dataset.size = row.size;
        cell.dataset.measurement = column.id;
        cell.setAttribute('aria-label', `${row.size}, ${column.label}, ${row.values[column.id]} ${this.state.unit}`);
        cell.setAttribute('aria-selected', 'false');
        cell.addEventListener('mouseenter', () => this.#setHover(row.size, column.id));
        cell.addEventListener('mouseleave', () => this.#setHover(null, null));
        cell.addEventListener('click', () => this.#select(row.size, column.id));
        cell.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.#select(row.size, column.id);
          }
        });
        tr.append(cell);
      });
      tbody.append(tr);
    });
    table.append(thead, tbody);
    region.append(table);
    this.#applyInteractionClasses();
  }

  #renderInstructions() {
    const list = this.querySelector('.rosselle-size-guide__measure-list');
    list.replaceChildren();
    this.payload.how_to_measure.forEach((item) => {
      list.append(createElement('dt', '', item.label), createElement('dd', '', item.instruction));
    });
  }

  #setHover(size, measurement) {
    this.hover = size && measurement ? { size, measurement } : null;
    this.#applyInteractionClasses();
  }

  #select(size, measurement) {
    this.hover = null;
    this.state.activeSize = size;
    this.state.activeMeasurement = measurement;
    this.#applyInteractionClasses();
  }

  #applyInteractionClasses() {
    const cross = this.hover ||
      (this.state.activeSize && this.state.activeMeasurement
        ? { size: this.state.activeSize, measurement: this.state.activeMeasurement }
        : null);

    this.querySelectorAll('[data-size]').forEach((node) => {
      node.classList.toggle('is-row-active', cross?.size === node.dataset.size);
      node.classList.toggle('is-preselected', !cross && this.state.preselectedSize === node.dataset.size);
    });
    this.querySelectorAll('[data-measurement]').forEach((node) => {
      node.classList.toggle('is-column-active', cross?.measurement === node.dataset.measurement);
    });
    this.querySelectorAll('td[data-size][data-measurement]').forEach((node) => {
      const selected = Boolean(
        cross && cross.size === node.dataset.size && cross.measurement === node.dataset.measurement
      );
      node.classList.toggle('is-intersection', selected);
      node.setAttribute('aria-selected', selected ? 'true' : 'false');
    });
  }
}

if (!customElements.get('rosselle-size-guide')) {
  customElements.define('rosselle-size-guide', RosselleSizeGuide);
}
